/**
 * MemoryDrawer — slide-in panel for editing the global per-user memory.
 *
 * "Global memory" is a free-form instruction the user gives the agent that
 * persists across every session (e.g. "Always answer with two-decimal numbers"
 * or "I'm tracking the 2026 AY only").  Backend endpoint: GET/POST /api/chat/v2/memory.
 *
 * Built on Radix Dialog (positioned as a right-edge sheet) — same primitive
 * the ConfirmDialog uses, so we get focus trap, ESC dismiss, and scroll lock
 * for free.
 */
import { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import * as chatService from "../../services/chatService";
import { toast } from "../../stores/toastStore";

export default function MemoryDrawer({ open, onOpenChange }) {
  const [memory,  setMemory]  = useState("");
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);

  // Lazy-load memory only when the drawer first opens.  Subsequent opens
  // re-fetch so a server-side change isn't masked by a stale local copy.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    chatService.getMemory().then(
      (m) => alive && setMemory(m),
      (err) => {
        if (alive) toast.error(err.message || "Couldn't load memory");
      },
    ).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open]);

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await chatService.saveMemory(memory);
      toast.success("Memory saved");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Couldn't save memory");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="
            fixed inset-0 z-40
            bg-[var(--color-ink)]/30
            data-[state=open]:animate-[fadeIn_200ms_ease-out]
          "
        />
        <Dialog.Content
          className="
            fixed top-0 right-0 bottom-0 z-50
            w-[min(28rem,calc(100vw-1rem))]
            bg-[var(--color-paper)]
            border-l border-[var(--color-rule-strong)]
            outline-none
            data-[state=open]:animate-[slideInFromRight_280ms_cubic-bezier(0.22,1,0.36,1)]
            flex flex-col
          "
        >
          <header className="flex items-center justify-between px-7 pt-7 pb-5 border-b border-[var(--color-rule)]">
            <div>
              <div className="eyebrow">PERSISTENT INSTRUCTION</div>
              <Dialog.Title className="display text-[var(--text-2xl)] leading-[1.1] mt-3">
                Global memory
              </Dialog.Title>
              <Dialog.Description className="text-[var(--text-sm)] text-[var(--color-ink-2)] mt-2">
                Free-form context the agent inherits in every session.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="p-2 hover:bg-[var(--color-paper-2)] transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4 text-[var(--color-ink-2)]" />
              </button>
            </Dialog.Close>
          </header>

          <form onSubmit={onSave} className="flex-1 flex flex-col">
            <div className="flex-1 px-7 py-6 overflow-y-auto">
              <textarea
                value={memory}
                onChange={(e) => setMemory(e.target.value)}
                disabled={loading}
                rows={14}
                placeholder={
                  loading
                    ? "Loading current memory…"
                    : "e.g. Always format numeric answers as a table.\nMy area is SADT-CS specifically.\nPrefer Active sections only when in doubt."
                }
                className="
                  w-full resize-none
                  bg-transparent
                  border border-[var(--color-rule-strong)]
                  px-4 py-3
                  text-[var(--text-base)] text-[var(--color-ink)]
                  placeholder:text-[var(--color-ink-3)]
                  outline-none
                  focus:border-[var(--color-accent)]
                  transition-colors
                "
              />
              <p className="mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)] mt-3">
                Saved memory applies to every department you work in.
              </p>
            </div>

            <footer className="px-7 py-4 border-t border-[var(--color-rule)] flex items-center justify-end gap-3">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving || loading}
                className="btn btn-primary"
              >
                {saving ? "Saving…" : "Save memory"}
              </button>
            </footer>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// Slide-in keyframe used by the drawer; co-located so Tailwind doesn't need
// to know about it.
if (typeof document !== "undefined" && !document.getElementById("memory-drawer-keyframes")) {
  const style = document.createElement("style");
  style.id = "memory-drawer-keyframes";
  style.textContent = `
    @keyframes slideInFromRight {
      from { transform: translateX(100%); }
      to   { transform: translateX(0); }
    }
  `;
  document.head.appendChild(style);
}
